import { keyBy, pick } from 'lodash';

import { SearchableFields } from '~/api/dataFields';
import { Group } from '~/components/Shared/Filters_DEPRECATED/types';
import { DataFieldType } from '~/enums/DataFieldType';
import { Field } from '~/evifields/src/types/Field';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { intlService } from '~/i18n/IntlService';

import {
  AlertSectionsResponse,
  getAlertSections,
  PilotField,
  PilotFieldSection,
  SearchableFieldId,
} from '../methods/alert';

type FilterSectionsResponse = {
  dataFields: PilotField[];
  searchableFields: PilotField[];
  fields: Record<FieldId, Field>;
  fieldGroups: Group[];
  defaultFilters: Filter[];
};

const addGroupId = (section: PilotFieldSection) => ({
  ...section,
  id: section.title,
});

export const toEviFieldType = (type: DataFieldType) => {
  switch (type) {
    case DataFieldType.ARRAY_MULTIPLE:
      return 'enum_set';
    case DataFieldType.ARRAY_SINGLE:
      return 'enum';
    case DataFieldType.ATTACHMENT:
      return 'file';
    case DataFieldType.BOOLEAN:
      return 'boolean';
    case DataFieldType.CLAUSE:
      return 'clause';
    case DataFieldType.DATE:
      return 'date';
    case DataFieldType.NUMBER:
      return 'number';
    case DataFieldType.ADDRESS:
    case DataFieldType.MONETARY:
      return 'text';
    case DataFieldType.STRING:
    case DataFieldType.TEXT_AREA:
    case DataFieldType.REFERENCE:
      return 'text';
    case DataFieldType.AGE:
      return 'age';
  }
};

const SearchableFieldToEviField = (pilotField: PilotField): Field => ({
  id: String(pilotField.id),
  ufFieldId: pilotField.uf_field_id,
  type: toEviFieldType(pilotField.type),
  label: pilotField.label,
  settings: {
    options: [],
  },
});

const mapSectionsToNormalizedFields = (sections: PilotFieldSection[] = []) => {
  const fields = sections
    .map(addGroupId)
    .flatMap((section) => section.fields)
    .map(SearchableFieldToEviField);

  return keyBy(fields, 'id');
};

const sectionToGroup = (section: PilotFieldSection): Group => {
  return {
    id: section.title,
    label: section.title,
    fieldIds: section.fields.map((f) => String(f.id)),
  };
};

const mapSectionsToNormalizedGroups = (sections: PilotFieldSection[] = []) =>
  sections.map(sectionToGroup);

const isPinned = (field: PilotField): boolean => !!field.is_pinned;
const toDefaultFilter = (field: PilotField): Filter => ({
  id: String(field.id),
  fieldId: String(field.id),
  operatorId: null,
  values: [],
});
const getDefaultFilters = (sections: PilotFieldSection[] = []) => {
  const defaultFilters = sections
    .flatMap((section) => section.fields)
    .filter(isPinned)
    .map(toDefaultFilter);

  return defaultFilters;
};

const pilotFieldFactory = ({
  id,
  type,
  name,
  label,
}: {
  id: SearchableFieldId;
  type: DataFieldType;
  name: string;
  label: string;
}): PilotField => {
  return {
    id,
    type,
    name,
    label,
    chained_fields: [],
    is_pinned: false,
  };
};
const getDocumentGroupField = (): PilotField =>
  pilotFieldFactory({
    id: 'document_group_id' as SearchableFields,
    type: 'ARRAY_MULTIPLE' as DataFieldType,
    name: 'Document Group',
    label: intlService.getIntl().formatMessage({
      id: 'searchTransformer.documentGroup',
      defaultMessage: 'Document Group',
      description: 'Label for document group filter',
    }),
  });

const getFolderField = (): PilotField =>
  pilotFieldFactory({
    id: 'folder_id' as SearchableFields,
    type: 'ARRAY_MULTIPLE' as DataFieldType,
    name: 'Folder',
    label: intlService.getIntl().formatMessage({
      id: 'searchTransformer.folder',
      defaultMessage: 'Folder',
      description: 'Label for folder filter',
    }),
  });

const getDefaultMoreFilters = () => ({
  document_group_id: SearchableFieldToEviField(getDocumentGroupField()),
  folder: {
    label: intlService.getIntl().formatMessage({
      id: 'searchTransformer.folders',
      defaultMessage: 'Folders',
      description: 'Label for folders filter',
    }),
    id: 'folder',
    type: 'folder',
    settings: {},
  },
  clause: {
    label: intlService.getIntl().formatMessage({
      id: 'searchTransformer.clauseType',
      defaultMessage: 'Clause Type',
      description: 'Label for clause type filter',
    }),
    id: 'clause',
    type: 'clause',
    settings: {},
  },
  filename_content: {
    label: intlService.getIntl().formatMessage({
      id: 'searchTransformer.textSearchFilenameContent',
      defaultMessage: 'Text Search: Filename and Content',
      description: 'Label for text search in filename and content',
    }),
    id: 'filename_content',
    type: 'text_search',
    settings: {},
  },
  filename: {
    label: intlService.getIntl().formatMessage({
      id: 'searchTransformer.textSearchFilename',
      defaultMessage: 'Text Search: Filename',
      description: 'Label for text search in filename only',
    }),
    id: 'filename',
    type: 'text_search',
    settings: {},
  },
  content: {
    label: intlService.getIntl().formatMessage({
      id: 'searchTransformer.textSearchContent',
      defaultMessage: 'Text Search: Content',
      description: 'Label for text search in content only',
    }),
    id: 'content',
    type: 'text_search',
    settings: {},
  },
});

// i18n-ignore: ID
const MORE_FILTERS_SECTION_ID = 'More filters';

const getMoreFiltersSection = (moreFiltersFieldIds: FieldId[]) => ({
  id: MORE_FILTERS_SECTION_ID,
  label: intlService.getIntl().formatMessage({
    id: 'searchTransformer.moreFiltersSectionLabel',
    defaultMessage: 'More filters',
    description: 'Label for more filters section',
  }),
  fieldIds: moreFiltersFieldIds,
});

export const getDefaultMoreFilterFieldIds = () =>
  Object.keys(getDefaultMoreFilters());

export const transformFieldSections = (
  sectionsResponse: AlertSectionsResponse,
  moreFiltersFieldIds: FieldId[] = getDefaultMoreFilterFieldIds(),
) => {
  const fields = mapSectionsToNormalizedFields(sectionsResponse.sections);
  const fieldsMap = Object.assign(
    fields,
    pick(getDefaultMoreFilters(), moreFiltersFieldIds),
  );

  return fieldsMap;
};

export const getFilterSections = async (
  moreFiltersFieldIds: FieldId[] = getDefaultMoreFilterFieldIds(),
): Promise<FilterSectionsResponse> => {
  const sectionsResponse = await getAlertSections();
  const dataFields = sectionsResponse.sections.flatMap(
    (section) => section.fields,
  );
  const searchableFields = sectionsResponse.sections.flatMap(
    (section) => section.fields,
  );
  searchableFields.push(getDocumentGroupField());
  searchableFields.push(getFolderField());

  const fields = mapSectionsToNormalizedFields(sectionsResponse.sections);

  const fieldGroups = mapSectionsToNormalizedGroups(sectionsResponse.sections);
  const defaultFilters = getDefaultFilters(sectionsResponse.sections);
  const fieldsMap = Object.assign(
    fields,
    pick(getDefaultMoreFilters(), moreFiltersFieldIds),
  );
  const groupset = fieldGroups.concat(
    getMoreFiltersSection(moreFiltersFieldIds),
  );

  return {
    dataFields,
    searchableFields, //superset of dataFields
    fields: fieldsMap,
    fieldGroups: groupset,
    defaultFilters,
  };
};
